import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Box, ButtonBase, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useState, type ReactNode } from 'react';
import { tr } from '../lib/i18n';

export const FORMTAGS_SUPPORT_EMAIL = 'hello@formtags.com';
/** International format, e.g. '+962 79 123 4567'. Empty = the phone and WhatsApp rows are hidden. */
export const FORMTAGS_SUPPORT_PHONE: string = '';

const digits = (s: string) => s.replace(/[^\d+]/g, '');

/** One way to reach us: a large, clear row (icon · what it is · the address). */
function Channel({ href, icon, title, value, external, color }: { href: string; icon: ReactNode; title: string; value: string; external?: boolean; color: string }) {
  const m3 = useTheme().palette.m3;
  return (
    <ButtonBase
      component="a"
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      sx={{
        width: '100%',
        justifyContent: 'flex-start',
        gap: 1.5,
        p: 1.25,
        borderRadius: 3,
        border: 1,
        borderColor: 'divider',
        textAlign: 'start',
        transition: 'background-color 120ms, border-color 120ms',
        '&:hover': { bgcolor: alpha(m3.onSurface, 0.04), borderColor: alpha(m3.onSurface, 0.24) },
        '&.Mui-focusVisible': { outline: `2px solid ${m3.primary}`, outlineOffset: 2 },
      }}
    >
      <Box sx={{ width: 40, height: 40, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: alpha(color, 0.1), color, flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} lineHeight={1.3}>{title}</Typography>
        <Typography variant="caption" color="text.secondary" dir="ltr" component="div" sx={{ textAlign: 'start' }} noWrap>{value}</Typography>
      </Box>
    </ButtonBase>
  );
}

/** FormTags support: a round button in a screen corner; it opens a short card with the ways to reach us. */
export function SupportWidget() {
  const m3 = useTheme().palette.m3;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const label = tr('دعم FormTags', 'FormTags support');
  const phone = FORMTAGS_SUPPORT_PHONE.trim();
  return (
    <>
      <Tooltip title={label}>
        <Stack
          component="button"
          type="button"
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={label}
          alignItems="center"
          justifyContent="center"
          sx={{
            position: 'fixed',
            // opposite the mobile "New entry" button, which sits on the inline-end edge
            insetInlineEnd: { xs: 'auto', md: 20 },
            insetInlineStart: { xs: 16, md: 'auto' },
            bottom: 'calc(16px + env(safe-area-inset-bottom))',
            zIndex: 1100,
            width: 52,
            height: 52,
            p: 0,
            border: 0,
            borderRadius: '50%',
            cursor: 'pointer',
            bgcolor: m3.primary,
            color: m3.onPrimary,
            boxShadow: '0 1px 3px rgba(0,0,0,.2), 0 4px 8px 3px rgba(0,0,0,.08)',
            transition: 'transform .15s, box-shadow .15s',
            '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 2px 4px rgba(0,0,0,.2), 0 6px 12px 4px rgba(0,0,0,.1)' },
            '&:focus-visible': { outline: `2px solid ${m3.primary}`, outlineOffset: 3 },
          }}
        >
          <SupportAgentOutlinedIcon />
        </Stack>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { mb: 1.5, width: 320, maxWidth: 'calc(100vw - 32px)', borderRadius: '20px', overflow: 'hidden', boxShadow: `0 12px 32px ${alpha(m3.onSurface, 0.18)}` } } }}
      >
        <Box sx={{ px: 2.25, pt: 2, pb: 1.75, bgcolor: m3.primary, color: m3.onPrimary, position: 'relative' }}>
          <IconButton size="small" onClick={() => setAnchor(null)} aria-label={tr('إغلاق', 'Close')} sx={{ position: 'absolute', top: 10, insetInlineEnd: 10, color: 'inherit', opacity: 0.8 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <SupportAgentOutlinedIcon sx={{ fontSize: 30, mb: 0.75, opacity: 0.9 }} />
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>{tr('محتاج مساعدة؟', 'Need a hand?')}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85, lineHeight: 1.6 }}>
            {tr('إذا واجهت أي مشكلة أو عندك سؤال عن النظام، لا تتردد تتواصل معنا — فريق FormTags جاهز يساعدك.', 'If anything goes wrong or you have a question about the system, reach out — the FormTags team is here to help.')}
          </Typography>
        </Box>
        <Stack spacing={1} sx={{ p: 1.5 }}>
          {phone && (
            <Channel
              href={`https://wa.me/${digits(phone).replace('+', '')}`}
              external
              icon={<WhatsAppIcon />}
              title={tr('راسلنا على واتساب', 'Message us on WhatsApp')}
              value={phone}
              color="#1DA851"
            />
          )}
          {phone && <Channel href={`tel:${digits(phone)}`} icon={<PhoneOutlinedIcon />} title={tr('اتصل فينا', 'Call us')} value={phone} color={m3.primary} />}
          <Channel href={`mailto:${FORMTAGS_SUPPORT_EMAIL}`} icon={<MailOutlineIcon />} title={tr('ابعتلنا إيميل', 'Email us')} value={FORMTAGS_SUPPORT_EMAIL} color={m3.primary} />
        </Stack>
      </Popover>
    </>
  );
}
